import type { ControlBridgeQueueItem } from './deriveViewModel';
import type { ResourceFilter } from './filters';
import { fixUtf8Mojibake } from './fixUtf8Mojibake';

export function priorityRank(priority?: string | number | null): number {
  if (priority === undefined || priority === null || priority === '') return 5;
  if (typeof priority === 'number') {
    if (!Number.isFinite(priority)) return 5;
    return Math.min(10, Math.max(0, Math.trunc(priority)));
  }
  const raw = String(priority).trim();
  if (!raw) return 5;
  const tagged = /^P(\d+)/i.exec(raw);
  if (tagged) {
    const n = Number(tagged[1]);
    if (!Number.isFinite(n)) return 5;
    return Math.min(10, Math.max(0, n));
  }
  if (/^\d+$/.test(raw)) {
    return Math.min(10, Math.max(0, Number(raw)));
  }
  return 5;
}

export function isP0(priority?: string | number | null): boolean {
  return priorityRank(priority) === 0;
}

export function isP1(priority?: string | number | null): boolean {
  return priorityRank(priority) === 1;
}

/** True for P0, P1, and P2 (numeric or Pn). */
export function isPriorityP0ToP2(priority?: string | number | null): boolean {
  return priorityRank(priority) <= 2;
}

export function sortByPriority(items: ControlBridgeQueueItem[]): ControlBridgeQueueItem[] {
  return [...items].sort((a, b) => priorityRank(a.priority) - priorityRank(b.priority));
}

const TYPE_BUCKETS: Array<{ key: ResourceFilter; label: string }> = [
  { key: 'type:leadership', label: 'Leadership' },
  { key: 'type:growth', label: 'Growth' },
  { key: 'type:success', label: 'Success' },
  { key: 'type:engineering', label: 'Engineering' },
];

/** Static fallback chips when manifest has no resource tokens yet. */
export const RESOURCE_TYPE_OPTIONS: Array<{ key: ResourceFilter; label: string }> = [
  { key: 'all', label: 'All resources' },
  ...TYPE_BUCKETS,
];

const TYPE_BUCKET_TOKENS = new Set(['leadership', 'growth', 'success', 'engineering']);
const RAW_REF_PATTERN = /^(ai-rp-|ai-op-resource-profile-)/i;

/** Display name for resource tokens — never surface raw ai-rp-* / ai-op-* refs. */
export function resolveResourceDisplayName(token: string, label?: string): string {
  const trimmedLabel = label?.trim();
  if (trimmedLabel && !RAW_REF_PATTERN.test(trimmedLabel)) {
    return fixUtf8Mojibake(trimmedLabel);
  }
  let slug = token.trim();
  if (slug.startsWith('ai-op-resource-profile-')) {
    slug = slug.slice('ai-op-resource-profile-'.length);
  } else if (slug.toLowerCase().startsWith('ai-rp-')) {
    slug = slug.slice('ai-rp-'.length);
  } else if (slug.toLowerCase().startsWith('ai-ddd-rp-')) {
    slug = slug.slice('ai-ddd-rp-'.length);
  } else if (slug.toLowerCase().startsWith('co-prh-')) {
    slug = slug.slice('co-prh-'.length);
  } else {
    const pra = /^co-[a-z0-9]+-pra-(.+)$/i.exec(slug);
    if (pra?.[1]) slug = pra[1];
  }
  return slug
    .replace(/[-_]/g, ' ')
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');
}

/** Dedupe key for the same person appearing under multiple ref tokens. */
export function resourceIdentityKey(token: string, label?: string): string {
  return resolveResourceDisplayName(token, label).toLowerCase();
}

/** Build resource filter chips from manifest queue items (types + named resources). */
export function buildResourceOptions(items: ControlBridgeQueueItem[]): Array<{ key: ResourceFilter; label: string }> {
  const options: Array<{ key: ResourceFilter; label: string }> = [{ key: 'all', label: 'All resources' }];
  const seenTypes = new Set<string>();
  const people: Array<{ token: string; label: string; identity: string }> = [];

  for (const item of items) {
    let token = (item.res || item.assignedResourceRef)?.trim().toLowerCase() || '';
    // assignedResourceRef may already be person:<ref>; strip so chips stay person:<ref> not person:person:<ref>.
    while (token.startsWith('person:')) {
      token = token.slice(7).trim();
    }
    if (!token) continue;
    if (TYPE_BUCKET_TOKENS.has(token)) {
      seenTypes.add(token);
      continue;
    }
    const label = resolveResourceDisplayName(token, item.assignedResourceLabel);
    const identity = resourceIdentityKey(token, item.assignedResourceLabel);
    if (!people.some((p) => p.identity === identity)) {
      people.push({ token, label, identity });
    }
  }

  for (const bucket of TYPE_BUCKETS) {
    const token = bucket.key.slice(5);
    if (seenTypes.has(token)) {
      options.push(bucket);
    }
  }

  for (const person of people.sort((a, b) => a.label.localeCompare(b.label))) {
    options.push({ key: `person:${person.token}` as ResourceFilter, label: person.label });
  }

  if (options.length === 1) {
    // Do not invent Leadership/Growth/… chips when the queue has no type tokens (X7).
    return options;
  }

  return options;
}

export function resourceFilterLabel(filter: ResourceFilter, items: ControlBridgeQueueItem[]): string {
  if (filter === 'all') return 'All resources';
  if (filter.startsWith('type:')) {
    return filter.slice(5).charAt(0).toUpperCase() + filter.slice(6);
  }
  if (filter.startsWith('person:')) {
    const token = filter.slice(7);
    const match = items.find((i) => i.res?.toLowerCase() === token);
    return resolveResourceDisplayName(token, match?.assignedResourceLabel);
  }
  return filter;
}
